import fetch from 'isomorphic-unfetch';
import env from '../utils/env';
import AuthApi from '../apis/AuthApi';
import { getToken } from '../utils/cookie';
import { Logger, logger } from '../utils/logger';
import { SKOLON_CLAIM_STALE } from '../utils/skolon';
import { isSchoolSsoToken } from '../utils/federated-login';
import { SCHOOL_SSO_PUPIL_SCOPE } from '../utils/schoolSso';

// DEV-794 — every silent renewal goes through here, so a school-SSO pupil
// (sub `oauth2|<conn>|`, conn on NEXT_PUBLIC_SCHOOL_SSO_CONNECTIONS) is renewed with the same narrow
// 'openid' scope they signed in with, and the renewed id_token carries no
// name or picture. Everyone else keeps the WebAuth default, unchanged.
// Keyed on the token being renewed: that is the identity checkSession will
// return.
export const renewTokenFor = (token: string, host: string): Promise<string> =>
  isSchoolSsoToken(token)
    ? AuthApi.renewToken(host, { scope: SCHOOL_SSO_PUPIL_SCOPE })
    : AuthApi.renewToken(host);

interface RequestContext {
  method: string;
  url: string;
  body?: Record<string, unknown>;
  timestamp: string;
  duration?: number;
}

interface DebugInfo {
  status: number;
  message: string;
  requestContext: {
    method: string;
    url: string;
    body: Record<string, unknown>;
    timestamp: string;
    duration?: number;
  } | null;
}

class CustomError extends Error {
  response: Response;
  status: number;
  // jakari's machine-readable error code (`code` in the body, NOT `error`,
  // which holds a generic message). Set for the Skolon activation call.
  code?: string;
  requestContext?: RequestContext;
  responseText?: string;

  constructor(response = null, requestContext?: RequestContext, ...params: string[]) {
    const errorMessage =
      params[0] ?? `HTTP ${response?.status ?? 'Unknown'} Error`;

    super(errorMessage);

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, CustomError);
    }

    this.name = 'CustomError';
    this.status = response?.status;
    this.response = response;
    this.requestContext = requestContext;
  }

  async getResponseText(): Promise<string> {
    if (this.responseText) {
      return this.responseText;
    }

    if (this.response) {
      try {
        const clonedResponse = this.response.clone();
        this.responseText = await clonedResponse.text();
        return this.responseText;
      } catch (e) {
        return 'Failed to read response text';
      }
    }

    return 'No response available';
  }

  getDebugInfo(): DebugInfo {
    return {
      status: this.status,
      message: this.message,
      requestContext: this.requestContext
        ? {
            method: this.requestContext.method,
            url: this.requestContext.url,
            body: this.sanitizeRequestBody(this.requestContext.body),
            timestamp: this.requestContext.timestamp,
            duration: this.requestContext.duration,
          }
        : null,
    };
  }

  private sanitizeRequestBody(
    body: Record<string, unknown>,
  ): Record<string, unknown> {
    if (!body) return body;

    const sanitized = { ...body };

    if (typeof sanitized.code === 'string') {
      sanitized.code = this.sanitizeVoucherCode(sanitized.code);
    }
    if (sanitized.token) {
      sanitized.token = '[REDACTED]';
    }

    return sanitized;
  }

  private sanitizeVoucherCode(code: string): string {
    if (!code || code.length < 4) return '[REDACTED]';
    return `${code.substring(0, 2)}***${code.substring(code.length - 2)}`;
  }
}

export const wrapReq = async (
  request: Promise<Response>,
  requestContext?: RequestContext,
) => {
  const response = await request;
  if (response.ok) {
    return response.json();
  }
  throw new CustomError(response, requestContext);
};

export const updateUser = async (token: string, locale: string) =>
  await wrapReq(
    fetch(`${env.URL_API_ROOT}/user`, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        cookie: `token=${token}`,
        'Content-Type': 'application/json',
        authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ locale }),
    }),
  );

export const verifyUser = async (
  token = getToken(),
  host?: string,
  n = 2,
): Promise<User> => {
  const url = `${env.URL_API_ROOT}/user`;
  const response = await fetch(url, {
    credentials: 'include',
    headers: {
      cookie: `token=${token}`,
      authorization: `Bearer ${token}`,
    },
  });

  if (response.status === 401 && n > 1 && host) {
    Logger.tokenRenewalAttempt('401 error during user verification');
    const newToken = await renewTokenFor(token, host);
    return verifyUser(newToken, host, n - 1);
  }
  if (response.ok) {
    return response.json();
  }
  throw new CustomError(response);
};

interface ActivateUser {
  (
    token: string,
    code: string,
    locale: string,
    host: string,
    n?: number,
  ): Promise<User>;
}

export const activateUser: ActivateUser = async (
  token,
  code,
  locale,
  host,
  n = 2,
) => {
  const startTime = Date.now();
  const url = `${env.URL_API_ROOT}/user/activate`;
  const requestBody = { code, locale };

  const requestContext: RequestContext = {
    method: 'POST',
    url,
    body: requestBody,
    timestamp: new Date().toISOString(),
  };

  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: {
      cookie: `token=${token}`,
      'Content-Type': 'application/json',
      authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(requestBody),
  });

  requestContext.duration = Date.now() - startTime;

  if (response.status === 401 && n > 1) {
    Logger.tokenRenewalAttempt('401 error during user activation');
    const newToken = await renewTokenFor(token, host);
    return activateUser(newToken , code, locale, host, n - 1);
  }
  if (response.ok) {
    return response.json();
  }
  throw new CustomError(response, requestContext);
};

// DEV-709. The /voucher page is where a pupil whose subscription lapsed lands,
// and it used to offer only a helpdesk article — which throws away the one thing
// we already have here. This user is authenticated: _app.tsx only redirects to
// /voucher AFTER verifyUser(token) succeeds and reports `inactive`. So instead of
// asking support to work out who wrote in, we record the request against their id.
//
// Goes to the same jakari route toosa uses, which lands in the same
// account_deletion_requests table and the same support workflow. jakari derives
// the user from `req.auth.sub` and ignores any client-supplied id, so there is
// nothing to pass and nothing to spoof. Those routes are deliberately not behind
// validateVoucher precisely so an expired user can still reach them.
export const requestAccountDeletion = async (
  token: string,
  host: string,
  n = 2,
): Promise<void> => {
  const startTime = Date.now();
  const url = `${env.URL_API_ROOT}/account/deletion`;

  const requestContext: RequestContext = {
    method: 'POST',
    url,
    body: {},
    timestamp: new Date().toISOString(),
  };

  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: {
      cookie: `token=${token}`,
      'Content-Type': 'application/json',
      authorization: `Bearer ${token}`,
    },
  });

  requestContext.duration = Date.now() - startTime;

  // Same renewal retry as activateUser: a lapsed user sitting on this page is
  // exactly who is likely to be holding a token that just expired.
  if (response.status === 401 && n > 1) {
    Logger.tokenRenewalAttempt('401 error during account deletion request');
    const newToken = await renewTokenFor(token, host);
    return requestAccountDeletion(newToken, host, n - 1);
  }

  if (!response.ok) {
    throw new CustomError(response, requestContext);
  }
};

// DEV-677. Silent activation for a user who arrived through the Skolon tile.
// No body on purpose: jakari picks the voucher server-side from the Skolon
// claim on the VALIDATED id_token (`https://somistech.fi/skolon`), so the
// client never supplies a code and a pupil can't redeem the teacher voucher.
// Idempotent on jakari's side — an already-active user gets 200 again.
// Non-2xx throws CustomError with `status` and jakari's `code`;
// /login-skolon branches on those.
// jakari answers `{ error, code, timestamp }` (st-jakari#372). Read the
// machine code off a clone so the caller can still read the body.
const readJakariCode = async (response: Response): Promise<string> => {
  try {
    const body = await response.clone().json();
    return typeof body?.code === 'string' ? body.code : undefined;
  } catch {
    return undefined;
  }
};

export const activateSkolonUser = async (
  token: string,
  host: string,
  n = 2,
): Promise<User> => {
  const startTime = Date.now();
  const url = `${env.URL_API_ROOT}/user/skolon/activate`;

  const requestContext: RequestContext = {
    method: 'POST',
    url,
    body: {},
    timestamp: new Date().toISOString(),
  };

  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: {
      cookie: `token=${token}`,
      authorization: `Bearer ${token}`,
    },
  });

  requestContext.duration = Date.now() - startTime;

  if (response.ok) {
    return response.json();
  }

  const code = await readJakariCode(response);

  // A stale Skolon claim must NEVER be renewed silently. jakari (bb7e591)
  // requires `auth_time` and has dropped its `iat` fallback, so the gate is
  // escapable only by a real re-authentication — checkSession() keeps the
  // old `auth_time` and would fail the same check every time, looping until
  // the page's restart budget stops it (and that budget needs storage,
  // which a locked-down browser may deny). This is the only function that
  // calls the Skolon route, so this one check covers the whole path. The
  // page restarts SSO with prompt=login + max_age instead.
  if (response.status === 401 && n > 1 && code !== SKOLON_CLAIM_STALE) {
    Logger.tokenRenewalAttempt('401 error during Skolon activation');
    const newToken = await renewTokenFor(token, host);
    return activateSkolonUser(newToken, host, n - 1);
  }

  const error = new CustomError(response, requestContext);
  error.code = code;
  throw error;
};

const readSsoErrorField = async (
  response: Response,
): Promise<string | undefined> => {
  try {
    const body = await response.clone().json();
    return typeof body?.error === 'string' && body.error.startsWith('sso_')
      ? body.error
      : undefined;
  } catch {
    return undefined;
  }
};

// DEV-794. Silent activation for a pupil who signed in through a school's own
// Auth0 connection (e.g. `crosthwaite-google`). Same contract as
// activateSkolonUser: no body, because jakari takes the connection from the
// VERIFIED token's `sub` (`oauth2|<connection>|…`) and identify picks the
// voucher bound to that connection, so the client can never choose a code.
// Non-2xx throws CustomError with `status` and jakari's `code`
// (sso_not_configured 404, sso_seats_exhausted 409, sso_access_ended 410,
// sso_product_not_allowed 422, sso_connection_invalid 403); /login-school
// branches on the status.
//
// Unlike Skolon there is no freshness claim, so a plain 401 is simply an
// expired id_token: renew once (the Google connection is redirect-based and
// leaves an Auth0 SSO session, so checkSession can succeed) and retry.
//
// A 502/504 or a network error is retried once too, with the same token
// (review 2026-10-23, R1). jakari gives identify 5 s and answers 502 when
// that runs out — possibly AFTER identify committed the seat — and the call
// is idempotent (a second activation of the same sub finds the seat it
// already holds), so one retry turns a slow-Wi-Fi blip into a success
// instead of "Ask your teacher". Separate from the 401 budget; at most one
// of each.
//
// Resolves with the user AND the token that activation actually succeeded
// with (review 2026-10-26, S2). After a 401 that is the renewed token, not
// the one passed in: /login-school must persist THAT one server-side.
// renewToken's own document.cookie write cannot replace toosa's HttpOnly
// cookie, so storing the original would hand `/` an expired token — verify
// 401, another SSO round trip, and possibly the start-loop guard.
const TRANSIENT_ACTIVATION_STATUSES = [502, 504];

export type SchoolSsoActivation = { user: User; token: string };

export const activateSchoolSso = async (
  token: string,
  host: string,
  n = 2,
  transientRetries = 1,
): Promise<SchoolSsoActivation> => {
  const startTime = Date.now();
  const url = `${env.URL_API_ROOT}/user/sso/activate`;

  const requestContext: RequestContext = {
    method: 'POST',
    url,
    body: {},
    timestamp: new Date().toISOString(),
  };

  let response: Response;
  try {
    response = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: {
        cookie: `token=${token}`,
        authorization: `Bearer ${token}`,
      },
    });
  } catch (err) {
    if (transientRetries > 0) {
      return activateSchoolSso(token, host, n, transientRetries - 1);
    }
    throw err;
  }

  requestContext.duration = Date.now() - startTime;

  if (response.ok) {
    return { user: await response.json(), token };
  }

  if (
    TRANSIENT_ACTIVATION_STATUSES.includes(response.status) &&
    transientRetries > 0
  ) {
    return activateSchoolSso(token, host, n, transientRetries - 1);
  }

  // jakari (st-jakari#377) puts the machine code in `code`; fall back to
  // `error` when it carries an `sso_*` code instead of a message.
  const code =
    (await readJakariCode(response)) ?? (await readSsoErrorField(response));

  if (response.status === 401 && n > 1) {
    Logger.tokenRenewalAttempt('401 error during school SSO activation');
    // Only ever called for a school-SSO pupil: always the narrow scope.
    const newToken = await AuthApi.renewToken(host, {
      scope: SCHOOL_SSO_PUPIL_SCOPE,
    });
    return activateSchoolSso(newToken, host, n - 1, transientRetries);
  }

  const error = new CustomError(response, requestContext);
  error.code = code;
  throw error;
};

interface GetVoucherInfo {
  (
    token: string,
    code: string,
    locale: string,
    host: string,
    n?: number,
  ): Promise<{ product: string }>;
}

export const getVoucherInfo: GetVoucherInfo = async (
  token,
  code,
  locale,
  host,
  n = 2,
) => {
  const startTime = Date.now();
  const url = `${env.URL_API_ROOT}/voucher/${code}`;

  const requestContext: RequestContext = {
    method: 'GET',
    url,
    body: { voucherCode: code },
    timestamp: new Date().toISOString(),
  };

  const resolvedToken = token ?? getToken();
  const response = await fetch(url, {
    credentials: 'include',
    headers: {
      cookie: `token=${resolvedToken}`,
      authorization: `Bearer ${resolvedToken}`,
    },
  });

  requestContext.duration = Date.now() - startTime;

  if (response.status === 401 && n > 1) {
    Logger.tokenRenewalAttempt('401 error during voucher validation');
    const newToken = await renewTokenFor(resolvedToken, host);
    return getVoucherInfo(newToken , code, locale, host, n - 1);
  }
  if (response.ok) {
    return response.json();
  }
  throw new CustomError(response, requestContext);
};

// UK student auth — closed type-ahead school lookup.
//
// Public endpoint (no JWT required). Returns the unique matching
// school or null. Spec §1.4 + §3.3.
//
// 404 from the endpoint covers two cases that MUST be
// indistinguishable to callers: no match, or >1 match (ambiguity).
// Both return null here. Privacy: never reveal that other schools
// exist or how many matched.
//
// Also returns null when the input is too short (<3 chars) — the
// API returns 400 in that case but the UX is "wait for more input,"
// indistinguishable from "no match." Saves an HTTP call for early
// keystrokes too.
// DEV-735. loginMethods is the per-role door configuration from identify's
// school_login_method table. A missing role means that door is not offered
// for this school, and callers must not substitute a default (identify
// docs/adr/0001). Optional on the type because an identify deployed before
// DEV-734 omits the field entirely, which reads the same way: no doors.
export type SchoolLoginMethod =
  | 'passwordless'
  | 'roster_phrase'
  | 'microsoft'
  // DEV-794 / DEV-807: school Google Workspace sign-in.
  | 'google';

// DEV-794. One configured way in for a role. identify's `school_login_method`
// now keys on (school, role, method), so a role can have several doors, and
// `connection` names the Auth0 connection a door uses when it is not the
// default one for its method (e.g. a per-school `oauth2` Google connection
// for pupils). null means "use the method's default connection".
export interface LoginDoor {
  method: SchoolLoginMethod;
  connection: string | null;
}

export type LoginDoors = Partial<Record<'pupil' | 'staff', LoginDoor[]>>;

export interface SchoolMatch {
  slug: string;
  displayName: string;
  authModel: 'passwordless' | 'roster_username' | 'voucher_self';
  // Single-valued and kept for backward compatibility: identify OMITS a role
  // here when it has more than one door, and `loginDoors` carries it.
  loginMethods?: Partial<Record<'pupil' | 'staff', SchoolLoginMethod>>;
  // Optional: an identify deployed before DEV-794 does not send it.
  loginDoors?: LoginDoors;
}

export const MIN_SCHOOL_LOOKUP_LENGTH = 3;

export const lookupSchool = async (
  query: string,
): Promise<SchoolMatch | null> => {
  const trimmed = (query || '').trim();
  if (trimmed.length < MIN_SCHOOL_LOOKUP_LENGTH) return null;

  // Same-origin proxy via peikko's Next.js API route — avoids CORS
  // when the browser would otherwise call identify cross-port in dev.
  // The proxy (pages/api/school-lookup.ts) forwards to identify
  // server-side. Matches peikko's existing convention (see
  // pages/api/passwordless-*.ts).
  const url = `/api/school-lookup?q=${encodeURIComponent(trimmed)}`;

  let response: Response;
  try {
    response = await fetch(url);
  } catch (e) {
    logger.error('lookupSchool: network error', e as Error);
    return null;
  }

  if (response.status === 200) {
    return (await response.json()) as SchoolMatch;
  }
  if (response.status === 404 || response.status === 400) {
    // 404 = no unique match; 400 = query too short. Both UX-equivalent.
    return null;
  }

  logger.error(
    `lookupSchool: unexpected status ${response.status}`,
    new Error('unexpected_status'),
  );
  return null;
};

export const recordAnalyticsOptOut = async (locale: string): Promise<void> => {
  const url = `${env.URL_API_ROOT}/analytics-opt-outs`;

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ locale }),
    });

    if (!response.ok) {
      console.error('Failed to record analytics opt-out:', response.status);
    }
  } catch (error) {
    console.error('Error recording analytics opt-out:', error);
  }
};
